/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {ɵglobal as global} from '@angular/core';
import {setRootDomAdapter} from '../dom/dom_adapter';

import {GenericBrowserDomAdapter} from './generic_browser_adapter';

const /** @type {?} */ _attrToPropMap = {
  'class': 'className',
  'innerHtml': 'innerHTML',
  'readonly': 'readOnly',
  'tabindex': 'tabIndex',
};

const /** @type {?} */ DOM_KEY_LOCATION_NUMPAD = 3;

// Map to convert some key or keyIdentifier values to what will be returned by getEventKey
const /** @type {?} */ _keyMap: {[k: string]: string} = {
  // The following values are here for cross-browser compatibility and to match the W3C standard
  // cf http://www.w3.org/TR/DOM-Level-3-Events-key/
  '\b': 'Backspace',
  '\t': 'Tab',
  '\x7F': 'Delete',
  '\x1B': 'Escape',
  'Del': 'Delete',
  'Esc': 'Escape',
  'Left': 'ArrowLeft',
  'Right': 'ArrowRight',
  'Up': 'ArrowUp',
  'Down': 'ArrowDown',
  'Menu': 'ContextMenu',
  'Scroll': 'ScrollLock',
  'Win': 'OS'
};

// There is a bug in Chrome for numeric keypad keys:
// https://code.google.com/p/chromium/issues/detail?id=155654
// 1, 2, 3 ... are reported as A, B, C ...
const /** @type {?} */ _chromeNumKeyPadMap = {
  'A': '1',
  'B': '2',
  'C': '3',
  'D': '4',
  'E': '5',
  'F': '6',
  'G': '7',
  'H': '8',
  'I': '9',
  'J': '*',
  'K': '+',
  'M': '-',
  'N': '.',
  'O': '/',
  '\x60': '0',
  '\x90': 'NumLock'
};

let /** @type {?} */ nodeContains: (a: any, b: any) => boolean;

if (global['Node']) {
  nodeContains = global['Node'].prototype.contains || function(node) {
    return !!(this.compareDocumentPosition(node) & 16);
  };
}
export class BrowserDomAdapter extends GenericBrowserDomAdapter {
/**
 * @param {?} templateHtml
 * @return {?}
 */
parse(templateHtml: string) { throw new Error('parse not implemented'); }
/**
 * @return {?}
 */
static makeCurrent() { setRootDomAdapter(new BrowserDomAdapter()); }
/**
 * @param {?} element
 * @param {?} name
 * @return {?}
 */
hasProperty(element: Node, name: string): boolean { return name in element; }
/**
 * @param {?} el
 * @param {?} name
 * @param {?} value
 * @return {?}
 */
setProperty(el: Node, name: string, value: any) { ( /** @type {?} */((<any>el)))[name] = value; }
/**
 * @param {?} el
 * @param {?} name
 * @return {?}
 */
getProperty(el: Node, name: string): any { return ( /** @type {?} */((<any>el)))[name]; }
/**
 * @param {?} el
 * @param {?} methodName
 * @param {?} args
 * @return {?}
 */
invoke(el: Node, methodName: string, args: any[]): any { ( /** @type {?} */((<any>el)))[methodName](...args); }
/**
 * @param {?} error
 * @return {?}
 */
logError(error: string): void {
    if (window.console) {
      if (console.error) {
        console.error(error);
      } else {
        console.log(error);
      }
    }
  }
/**
 * @param {?} error
 * @return {?}
 */
log(error: string): void {
    if (window.console) {
      window.console.log && window.console.log(error);
    }
  }
/**
 * @param {?} error
 * @return {?}
 */
logGroup(error: string): void {
    if (window.console) {
      window.console.group && window.console.group(error);
    }
  }
/**
 * @return {?}
 */
logGroupEnd(): void {
    if (window.console) {
      window.console.groupEnd && window.console.groupEnd();
    }
  }
/**
 * @return {?}
 */
get attrToPropMap(): any { return _attrToPropMap; }
/**
 * @param {?} nodeA
 * @param {?} nodeB
 * @return {?}
 */
contains(nodeA: any, nodeB: any): boolean { return nodeContains.call(nodeA, nodeB); }
/**
 * @param {?} el
 * @param {?} selector
 * @return {?}
 */
querySelector(el: Element, selector: string): any { return el.querySelector(selector); }
/**
 * @param {?} el
 * @param {?} selector
 * @return {?}
 */
querySelectorAll(el: any, selector: string): any[] { return el.querySelectorAll(selector); }
/**
 * @param {?} el
 * @param {?} evt
 * @param {?} listener
 * @return {?}
 */
on(el: Node, evt: any, listener: any) { el.addEventListener(evt, listener, false); }
/**
 * @param {?} el
 * @param {?} evt
 * @param {?} listener
 * @return {?}
 */
onAndCancel(el: Node, evt: any, listener: any): Function {
    el.addEventListener(evt, listener, false);
    // Needed to follow Dart's subscription semantic, until fix of
    // https://code.google.com/p/dart/issues/detail?id=17406
    return () => { el.removeEventListener(evt, listener, false); };
  }
/**
 * @param {?} el
 * @param {?} evt
 * @return {?}
 */
dispatchEvent(el: Node, evt: any) { el.dispatchEvent(evt); }
/**
 * @param {?} eventType
 * @return {?}
 */
createMouseEvent(eventType: string): MouseEvent {
    const /** @type {?} */ evt: MouseEvent = document.createEvent('MouseEvent');
    evt.initEvent(eventType, true, true);
    return evt;
  }
/**
 * @param {?} eventType
 * @return {?}
 */
createEvent(eventType: any): Event {
    const /** @type {?} */ evt: Event = document.createEvent('Event');
    evt.initEvent(eventType, true, true);
    return evt;
  }
/**
 * @param {?} evt
 * @return {?}
 */
preventDefault(evt: Event) {
    evt.preventDefault();
    evt.returnValue = false;
  }
/**
 * @param {?} evt
 * @return {?}
 */
isPrevented(evt: Event): boolean {
    return evt.defaultPrevented || evt.returnValue != null && !evt.returnValue;
  }
/**
 * @param {?} el
 * @return {?}
 */
getInnerHTML(el: HTMLElement): string { return el.innerHTML; }
/**
 * @param {?} el
 * @return {?}
 */
getTemplateContent(el: Node): Node|null {
    return 'content' in el && el instanceof HTMLTemplateElement ? el.content : null;
  }
/**
 * @param {?} el
 * @return {?}
 */
getOuterHTML(el: HTMLElement): string { return el.outerHTML; }
/**
 * @param {?} node
 * @return {?}
 */
nodeName(node: Node): string { return node.nodeName; }
/**
 * @param {?} node
 * @return {?}
 */
nodeValue(node: Node): string|null { return node.nodeValue; }
/**
 * @param {?} node
 * @return {?}
 */
type(node: HTMLInputElement): string { return node.type; }
/**
 * @param {?} node
 * @return {?}
 */
content(node: Node): Node {
    if (this.hasProperty(node, 'content')) {
      return ( /** @type {?} */((<any>node))).content;
    } else {
      return node;
    }
  }
/**
 * @param {?} el
 * @return {?}
 */
firstChild(el: Node): Node|null { return el.firstChild; }
/**
 * @param {?} el
 * @return {?}
 */
nextSibling(el: Node): Node|null { return el.nextSibling; }
/**
 * @param {?} el
 * @return {?}
 */
parentElement(el: Node): Node|null { return el.parentNode; }
/**
 * @param {?} el
 * @return {?}
 */
childNodes(el: any): Node[] { return el.childNodes; }
/**
 * @param {?} el
 * @return {?}
 */
childNodesAsList(el: Node): any[] {
    const /** @type {?} */ childNodes = el.childNodes;
    const /** @type {?} */ res = new Array(childNodes.length);
    for (let /** @type {?} */ i = 0; i < childNodes.length; i++) {
      res[i] = childNodes[i];
    }
    return res;
  }
/**
 * @param {?} el
 * @return {?}
 */
clearNodes(el: Node) {
    while (el.firstChild) {
      el.removeChild(el.firstChild);
    }
  }
/**
 * @param {?} el
 * @param {?} node
 * @return {?}
 */
appendChild(el: Node, node: Node) { el.appendChild(node); }
/**
 * @param {?} el
 * @param {?} node
 * @return {?}
 */
removeChild(el: Node, node: Node) { el.removeChild(node); }
/**
 * @param {?} el
 * @param {?} newChild
 * @param {?} oldChild
 * @return {?}
 */
replaceChild(el: Node, newChild: Node, oldChild: Node) { el.replaceChild(newChild, oldChild); }
/**
 * @param {?} node
 * @return {?}
 */
remove(node: Node): Node {
    if (node.parentNode) {
      node.parentNode.removeChild(node);
    }
    return node;
  }
/**
 * @param {?} parent
 * @param {?} ref
 * @param {?} node
 * @return {?}
 */
insertBefore(parent: Node, ref: Node, node: Node) { parent.insertBefore(node, ref); }
/**
 * @param {?} parent
 * @param {?} ref
 * @param {?} nodes
 * @return {?}
 */
insertAllBefore(parent: Node, ref: Node, nodes: Node[]) {
    nodes.forEach((n: any) => parent.insertBefore(n, ref));
  }
/**
 * @param {?} parent
 * @param {?} ref
 * @param {?} node
 * @return {?}
 */
insertAfter(parent: Node, ref: Node, node: any) { parent.insertBefore(node, ref.nextSibling); }
/**
 * @param {?} el
 * @param {?} value
 * @return {?}
 */
setInnerHTML(el: Element, value: string) { el.innerHTML = value; }
/**
 * @param {?} el
 * @return {?}
 */
getText(el: Node): string|null { return el.textContent; }
/**
 * @param {?} el
 * @param {?} value
 * @return {?}
 */
setText(el: Node, value: string) { el.textContent = value; }
/**
 * @param {?} el
 * @return {?}
 */
getValue(el: any): string { return el.value; }
/**
 * @param {?} el
 * @param {?} value
 * @return {?}
 */
setValue(el: any, value: string) { el.value = value; }
/**
 * @param {?} el
 * @return {?}
 */
getChecked(el: any): boolean { return el.checked; }
/**
 * @param {?} el
 * @param {?} value
 * @return {?}
 */
setChecked(el: any, value: boolean) { el.checked = value; }
/**
 * @param {?} text
 * @return {?}
 */
createComment(text: string): Comment { return document.createComment(text); }
/**
 * @param {?} html
 * @return {?}
 */
createTemplate(html: any): HTMLElement {
    const /** @type {?} */ t = document.createElement('template');
    t.innerHTML = html;
    return t;
  }
/**
 * @param {?} tagName
 * @param {?=} doc
 * @return {?}
 */
createElement(tagName: string, doc = document): HTMLElement { return doc.createElement(tagName); }
/**
 * @param {?} ns
 * @param {?} tagName
 * @param {?=} doc
 * @return {?}
 */
createElementNS(ns: string, tagName: string, doc = document): Element {
    return doc.createElementNS(ns, tagName);
  }
/**
 * @param {?} text
 * @param {?=} doc
 * @return {?}
 */
createTextNode(text: string, doc = document): Text { return doc.createTextNode(text); }
/**
 * @param {?} attrName
 * @param {?} attrValue
 * @param {?=} doc
 * @return {?}
 */
createScriptTag(attrName: string, attrValue: string, doc = document): HTMLScriptElement {
    const /** @type {?} */ el = /** @type {?} */(( <HTMLScriptElement>doc.createElement('SCRIPT')));
    el.setAttribute(attrName, attrValue);
    return el;
  }
/**
 * @param {?} css
 * @param {?=} doc
 * @return {?}
 */
createStyleElement(css: string, doc = document): HTMLStyleElement {
    const /** @type {?} */ style = /** @type {?} */(( <HTMLStyleElement>doc.createElement('style')));
    this.appendChild(style, this.createTextNode(css));
    return style;
  }
/**
 * @param {?} el
 * @return {?}
 */
createShadowRoot(el: HTMLElement): DocumentFragment { return ( /** @type {?} */((<any>el))).createShadowRoot(); }
/**
 * @param {?} el
 * @return {?}
 */
getShadowRoot(el: HTMLElement): DocumentFragment { return ( /** @type {?} */((<any>el))).shadowRoot; }
/**
 * @param {?} el
 * @return {?}
 */
getHost(el: HTMLElement): HTMLElement { return ( /** @type {?} */((<any>el))).host; }
/**
 * @param {?} node
 * @return {?}
 */
clone(node: Node): Node { return node.cloneNode(true); }
/**
 * @param {?} element
 * @param {?} name
 * @return {?}
 */
getElementsByClassName(element: any, name: string): HTMLElement[] {
    return element.getElementsByClassName(name);
  }
/**
 * @param {?} element
 * @param {?} name
 * @return {?}
 */
getElementsByTagName(element: any, name: string): HTMLElement[] {
    return element.getElementsByTagName(name);
  }
/**
 * @param {?} element
 * @return {?}
 */
classList(element: any): any[] { return Array.prototype.slice.call(element.classList, 0); }
/**
 * @param {?} element
 * @param {?} className
 * @return {?}
 */
addClass(element: any, className: string) { element.classList.add(className); }
/**
 * @param {?} element
 * @param {?} className
 * @return {?}
 */
removeClass(element: any, className: string) { element.classList.remove(className); }
/**
 * @param {?} element
 * @param {?} className
 * @return {?}
 */
hasClass(element: any, className: string): boolean {
    return element.classList.contains(className);
  }
/**
 * @param {?} element
 * @param {?} styleName
 * @param {?} styleValue
 * @return {?}
 */
setStyle(element: any, styleName: string, styleValue: string) {
    element.style[styleName] = styleValue;
  }
/**
 * @param {?} element
 * @param {?} stylename
 * @return {?}
 */
removeStyle(element: any, stylename: string) {
    // IE requires '' instead of null
    // see https://github.com/angular/angular/issues/7916
    element.style[stylename] = '';
  }
/**
 * @param {?} element
 * @param {?} stylename
 * @return {?}
 */
getStyle(element: any, stylename: string): string { return element.style[stylename]; }
/**
 * @param {?} element
 * @param {?} styleName
 * @param {?=} styleValue
 * @return {?}
 */
hasStyle(element: any, styleName: string, styleValue?: string|null): boolean {
    const /** @type {?} */ value = this.getStyle(element, styleName) || '';
    return styleValue ? value == styleValue : value.length > 0;
  }
/**
 * @param {?} element
 * @return {?}
 */
tagName(element: any): string { return element.tagName; }
/**
 * @param {?} element
 * @return {?}
 */
attributeMap(element: any): Map<string, string> {
    const /** @type {?} */ res = new Map<string, string>();
    const /** @type {?} */ elAttrs = element.attributes;
    for (let /** @type {?} */ i = 0; i < elAttrs.length; i++) {
      const /** @type {?} */ attrib = elAttrs[i];
      res.set(attrib.name, attrib.value);
    }
    return res;
  }
/**
 * @param {?} element
 * @param {?} attribute
 * @return {?}
 */
hasAttribute(element: Element, attribute: string): boolean {
    return element.hasAttribute(attribute);
  }
/**
 * @param {?} element
 * @param {?} ns
 * @param {?} attribute
 * @return {?}
 */
hasAttributeNS(element: Element, ns: string, attribute: string): boolean {
    return element.hasAttributeNS(ns, attribute);
  }
/**
 * @param {?} element
 * @param {?} attribute
 * @return {?}
 */
getAttribute(element: Element, attribute: string): string|null {
    return element.getAttribute(attribute);
  }
/**
 * @param {?} element
 * @param {?} ns
 * @param {?} name
 * @return {?}
 */
getAttributeNS(element: Element, ns: string, name: string): string {
    return element.getAttributeNS(ns, name);
  }
/**
 * @param {?} element
 * @param {?} name
 * @param {?} value
 * @return {?}
 */
setAttribute(element: Element, name: string, value: string) { element.setAttribute(name, value); }
/**
 * @param {?} element
 * @param {?} ns
 * @param {?} name
 * @param {?} value
 * @return {?}
 */
setAttributeNS(element: Element, ns: string, name: string, value: string) {
    element.setAttributeNS(ns, name, value);
  }
/**
 * @param {?} element
 * @param {?} attribute
 * @return {?}
 */
removeAttribute(element: Element, attribute: string) { element.removeAttribute(attribute); }
/**
 * @param {?} element
 * @param {?} ns
 * @param {?} name
 * @return {?}
 */
removeAttributeNS(element: Element, ns: string, name: string) {
    element.removeAttributeNS(ns, name);
  }
/**
 * @param {?} el
 * @return {?}
 */
templateAwareRoot(el: Node): any { return this.isTemplateElement(el) ? this.content(el) : el; }
/**
 * @return {?}
 */
createHtmlDocument(): HTMLDocument {
    return document.implementation.createHTMLDocument('fakeTitle');
  }
/**
 * @param {?} el
 * @return {?}
 */
getBoundingClientRect(el: Element): any {
    try {
      return el.getBoundingClientRect();
    } catch ( /** @type {?} */e) {
      return {top: 0, bottom: 0, left: 0, right: 0, width: 0, height: 0};
    }
  }
/**
 * @param {?} doc
 * @return {?}
 */
getTitle(doc: Document): string { return document.title; }
/**
 * @param {?} doc
 * @param {?} newTitle
 * @return {?}
 */
setTitle(doc: Document, newTitle: string) { document.title = newTitle || ''; }
/**
 * @param {?} n
 * @param {?} selector
 * @return {?}
 */
elementMatches(n: any, selector: string): boolean {
    if (n instanceof HTMLElement) {
      return n.matches && n.matches(selector) ||
          n.msMatchesSelector && n.msMatchesSelector(selector) ||
          n.webkitMatchesSelector && n.webkitMatchesSelector(selector);
    }

    return false;
  }
/**
 * @param {?} el
 * @return {?}
 */
isTemplateElement(el: Node): boolean {
    return el instanceof HTMLElement && el.nodeName == 'TEMPLATE';
  }
/**
 * @param {?} node
 * @return {?}
 */
isTextNode(node: Node): boolean { return node.nodeType === Node.TEXT_NODE; }
/**
 * @param {?} node
 * @return {?}
 */
isCommentNode(node: Node): boolean { return node.nodeType === Node.COMMENT_NODE; }
/**
 * @param {?} node
 * @return {?}
 */
isElementNode(node: Node): boolean { return node.nodeType === Node.ELEMENT_NODE; }
/**
 * @param {?} node
 * @return {?}
 */
hasShadowRoot(node: any): boolean {
    return node.shadowRoot != null && node instanceof HTMLElement;
  }
/**
 * @param {?} node
 * @return {?}
 */
isShadowRoot(node: any): boolean { return node instanceof DocumentFragment; }
/**
 * @param {?} node
 * @return {?}
 */
importIntoDoc(node: Node): any { return document.importNode(this.templateAwareRoot(node), true); }
/**
 * @param {?} node
 * @return {?}
 */
adoptNode(node: Node): any { return document.adoptNode(node); }
/**
 * @param {?} el
 * @return {?}
 */
getHref(el: Element): string { return ( /** @type {?} */((<any>el))).href; }
/**
 * @param {?} event
 * @return {?}
 */
getEventKey(event: any): string {
    let /** @type {?} */ key = event.key;
    if (key == null) {
      key = event.keyIdentifier;
      // keyIdentifier is defined in the old draft of DOM Level 3 Events implemented by Chrome and
      // Safari cf
      // http://www.w3.org/TR/2007/WD-DOM-Level-3-Events-20071221/events.html#Events-KeyboardEvents-Interfaces
      if (key == null) {
        return 'Unidentified';
      }
      if (key.startsWith('U+')) {
        key = String.fromCharCode(parseInt(key.substring(2), 16));
        if (event.location === DOM_KEY_LOCATION_NUMPAD && _chromeNumKeyPadMap.hasOwnProperty(key)) {
          // There is a bug in Chrome for numeric keypad keys:
          // https://code.google.com/p/chromium/issues/detail?id=155654
          // 1, 2, 3 ... are reported as A, B, C ...
          key = ( /** @type {?} */((_chromeNumKeyPadMap as any)))[key];
        }
      }
    }

    return _keyMap[key] || key;
  }
/**
 * @param {?} doc
 * @param {?} target
 * @return {?}
 */
getGlobalEventTarget(doc: Document, target: string): EventTarget|null {
    if (target === 'window') {
      return window;
    }
    if (target === 'document') {
      return document;
    }
    if (target === 'body') {
      return document.body;
    }
    return null;
  }
/**
 * @return {?}
 */
getHistory(): History { return window.history; }
/**
 * @return {?}
 */
getLocation(): Location { return window.location; }
/**
 * @param {?} doc
 * @return {?}
 */
getBaseHref(doc: Document): string|null {
    const /** @type {?} */ href = getBaseElementHref();
    return href == null ? null : relativePath(href);
  }
/**
 * @return {?}
 */
resetBaseElement(): void { baseElement = null; }
/**
 * @return {?}
 */
getUserAgent(): string { return window.navigator.userAgent; }
/**
 * @param {?} element
 * @param {?} name
 * @param {?} value
 * @return {?}
 */
setData(element: Element, name: string, value: string) {
    this.setAttribute(element, 'data-' + name, value);
  }
/**
 * @param {?} element
 * @param {?} name
 * @return {?}
 */
getData(element: Element, name: string): string|null {
    return this.getAttribute(element, 'data-' + name);
  }
/**
 * @param {?} element
 * @return {?}
 */
getComputedStyle(element: any): any { return getComputedStyle(element); }
/**
 * @return {?}
 */
supportsWebAnimation(): boolean {
    return typeof( /** @type {?} */((<any>Element))).prototype['animate'] === 'function';
  }
/**
 * @return {?}
 */
performanceNow(): number {
    // performance.now() is not available in all browsers, see
    // http://caniuse.com/#search=performance.now
    return window.performance && window.performance.now ? window.performance.now() :
                                                          new Date().getTime();
  }
/**
 * @return {?}
 */
supportsCookies(): boolean { return true; }
/**
 * @param {?} name
 * @return {?}
 */
getCookie(name: string): string|null { return parseCookieValue(document.cookie, name); }
/**
 * @param {?} name
 * @param {?} value
 * @return {?}
 */
setCookie(name: string, value: string) {
    // document.cookie is magical, assigning into it assigns/overrides one cookie value, but does
    // not clear other cookies.
    document.cookie = encodeURIComponent(name) + '=' + encodeURIComponent(value);
  }
}

let /** @type {?} */ baseElement: HTMLElement|null = null;
/**
 * @return {?}
 */
function getBaseElementHref(): string|null {
  if (!baseElement) {
    baseElement = /** @type {?} */(( document.querySelector('base')));
    if (!baseElement) {
      return null;
    }
  }
  return baseElement.getAttribute('href');
}

// based on urlUtils.js in AngularJS 1
let /** @type {?} */ urlParsingNode: any;
/**
 * @param {?} url
 * @return {?}
 */
function relativePath(url: any): string {
  if (!urlParsingNode) {
    urlParsingNode = document.createElement('a');
  }
  urlParsingNode.setAttribute('href', url);
  return (urlParsingNode.pathname.charAt(0) === '/') ? urlParsingNode.pathname :
                                                       '/' + urlParsingNode.pathname;
}
/**
 * @param {?} cookieStr
 * @param {?} name
 * @return {?}
 */
export function parseCookieValue(cookieStr: string, name: string): string|null {
  name = encodeURIComponent(name);
  for (const /** @type {?} */ cookie of cookieStr.split(';')) {
    const /** @type {?} */ eqIndex = cookie.indexOf('=');
    const [cookieName, cookieValue]: string[] =
        eqIndex == -1 ? [cookie, ''] : [cookie.slice(0, eqIndex), cookie.slice(eqIndex + 1)];
    if (cookieName.trim() === name) {
      return decodeURIComponent(cookieValue);
    }
  }
  return null;
}
